import { Fragment } from "react";
import type {
  CaseRecord,
  FindingActionKind,
  ValidationIssue,
} from "../../types";
import {
  buildCitationNumbering,
  indexRecords,
  splitSentences,
} from "../../lib/provenance";
import { renderFootnotes } from "../../lib/renderFootnotes";
import { findingId, isMappable } from "../../lib/findings";
import FindingMarker from "./FindingMarker";

// Unified content renderer used in BOTH the normal view and the
// "View sources" left panel: prose with subtle superscript citation
// footnotes (always visible) AND inline warning markers (placed
// sentence-level after the first sentence containing a finding's locator).
// Dismissed findings are omitted.
export default function FindingsProse({
  text,
  records,
  numbering,
  findings,
  actions,
  notes = {},
  onAction,
  onNavigateToRecord,
  onCite,
  activeRecordId = null,
}: {
  text: string;
  records: CaseRecord[];
  numbering?: Map<string, number>;
  findings: ValidationIssue[];
  actions: Record<string, FindingActionKind>;
  // finding id → the user's saved flag note (for prefill/display).
  notes?: Record<string, string>;
  onAction: (
    findingId: string,
    kind: FindingActionKind,
    note?: string,
  ) => void;
  // Normal view: jump to the record. Source view: onCite scrolls the
  // right panel (takes precedence when provided).
  onNavigateToRecord?: (recordId: string) => void;
  onCite?: (recordId: string) => void;
  activeRecordId?: string | null;
}) {
  const nums = numbering ?? buildCitationNumbering(text);
  const recordIndex = indexRecords(records);
  const tooltip = (id: string) => {
    const r = recordIndex.get(id);
    return r ? `${r.date}${r.subject ? ` — ${r.subject}` : ""}` : undefined;
  };
  const onCiteClick = onCite ?? onNavigateToRecord;

  const sentences = splitSentences(text);
  const pending = findings
    .map((f, i) => ({ f, id: findingId(f, i) }))
    .filter(({ f, id }) => isMappable(f) && actions[id] !== "dismissed");
  const placed = new Set<string>();

  const nodes = sentences.map((sentence, si) => {
    const lower = sentence.toLowerCase();
    const markers = pending.filter(
      ({ f, id }) =>
        !placed.has(id) && lower.includes((f.locator ?? "").toLowerCase()),
    );
    markers.forEach(({ id }) => placed.add(id));
    const sep = si < sentences.length - 1 ? " " : "";
    return (
      <Fragment key={si}>
        {renderFootnotes(sentence, {
          numbering: nums,
          tooltip,
          onClick: onCiteClick,
          activeRecordId,
          keyPrefix: `s${si}`,
        })}
        {markers.map(({ f, id }) => (
          <FindingMarker
            key={id}
            finding={f}
            action={actions[id]}
            note={notes[id]}
            onAction={(kind, note) => onAction(id, kind, note)}
          />
        ))}
        {sep}
      </Fragment>
    );
  });

  return <span className="findings-prose">{nodes}</span>;
}
